import React, { useContext, useState } from 'react';
import ExportModal from 'src/components/Modals/ExportModal';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { ExportDropdownOptionIds } from 'src/components/BeaconV2Components/common/AppExportModal/exportDataConfigData';
import { ExportContext } from 'src/providers/ExportContextProvider';
import { ExportProps, useBuildExportRequest } from 'src/utils/Hooks/useExportBuilder';
import { useTopicExportInjection } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicExports/useTopicExportInjection';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';

export const OptimizationGridExportModal = ({
  open,
  onClose,
  builderProps,
  options
}: {
  open: boolean;
  onClose: () => void;
  options: {
    displayName?: string;
    name?: string;
    id: string;
    label: string;
  }[];
  builderProps: ExportProps;
}) => {
  const [isIncludeComparison, setIsIncludeComparison] = useState(false);
  const [isSeparatedByWeek, setIsSeparatedByWeek] = useState(false);
  const queryParams = useAppSelector((state) => state.app.queryParams);
  const [selectedGroupBy, setSelectedGroupBy] = useState(options[0]);
  const { startPolling } = useContext(ExportContext);
  const [isExportButtonDisabled, setExportButtonDisabled] = useState(false);

  const { exportCallback } = useBuildExportRequest({
    ...builderProps,
    groupBy: selectedGroupBy.id
  });
  const { shouldIntercept, exportTopicData } = useTopicExportInjection();
  const { showSnackbar } = useSnackbar();

  const handleExportButton = () => {
    setExportButtonDisabled(true);

    if (shouldIntercept(selectedGroupBy.id)) {
      exportTopicData({
        isSeparatedByWeek: builderProps.isSeparateWeek,
        isIncludeComparison: builderProps.includeCompTimePeriod
      })
        .then(() => {
          startPolling();
          showSnackbar({ type: 'success', message: 'Your download is processing.' });
          onClose();
        })
        .catch((error) => {
          console.error('Error topic export failed:', error);
          showSnackbar({
            message: <GenericErrorMessage />,
            type: 'error'
          });
        })
        .finally(() => {
          setExportButtonDisabled(false);
        });
    } else {
      exportCallback()
        .then(() => {
          startPolling();
          showSnackbar({ type: 'success', message: 'Your download is processing.' });
          onClose();
        })
        .catch((error) => {
          console.error('Export failed:', error);
          showSnackbar({
            message: <GenericErrorMessage />,
            type: 'error'
          });
        })
        .finally(() => {
          setExportButtonDisabled(false);
        });
    }
  };

  const handleClose = () => {
    setIsIncludeComparison(false);
    setIsSeparatedByWeek(false);
    setSelectedGroupBy(options[0]);
    onClose();
  };

  const handleDropdownChange = (option: { id: string; label: string }) => {
    setSelectedGroupBy(option);
  };

  return (
    <ExportModal open={open} onClose={() => { }}>
      <ExportModal.Header title="Export Advertising Metrics" onClose={onClose} />
      <ExportModal.Body
        isSeparatedByWeek={isSeparatedByWeek}
        isIncludeComparison={isIncludeComparison}
        setIsSeparatedByWeek={setIsSeparatedByWeek}
        setIsIncludeComparison={setIsIncludeComparison}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <SlDropdownMenu
            defaultLabel={ExportDropdownOptionIds.PRODUCT}
            options={options}
            selectedId={selectedGroupBy?.id}
            onChange={(option) => handleDropdownChange(option as any)}
            stacklineButtonProps={{
              buttonSx: {
                minWidth: '99px',
                justifyContent: 'space-between'
              }
            }}
          />
        </div>
      </ExportModal.Body>
      <ExportModal.Footer
        searchParams={queryParams.searchParams}
        handleClose={handleClose}
        handleExportClick={handleExportButton}
        exportButtonDisabled={isExportButtonDisabled}
      />
    </ExportModal>
  );
};
